<template>
    <div class="enquiry-form">
        <TransitionModal>
            <Fixed
                v-if="isOpen"
                @close="close"
            >
                <ModalWithHeader
                    :label="formTitle"
                    @close="close"
                >
                    <EnquiryFormForm
                        :disabled="fetching"
                        :errors="errors"
                        hide-inputs-if-logged
                        @submit="submit"
                    >
                        <template #title>
                            {{ formTitle }}
                        </template>

                        <template #contact>
                            <FormRow>
                                <ThSelectVue
                                    v-model="purpose"
                                    :error="errors.last('input.purpose')"
                                    :legend="'Enquiry purpose'"
                                    :options="purposeOptions"
                                    :placeholder="'Enquiry purpose'"
                                    class="purpose"
                                />
                            </FormRow>

                            <FormRow>
                                <TextArea
                                    id="form-message"
                                    v-model="instructions"
                                    :error="errors.last('input.instructions')"
                                    :placeholder="'Please enter any questions, requirements or ideas you may have'"
                                    class="instructions"
                                    required
                                />
                            </FormRow>
                        </template>

                        <template #submit>
                            {{ submitLabel }}
                        </template>
                    </EnquiryFormForm>
                </ModalWithHeader>
            </Fixed>
        </TransitionModal>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import type {
    EnquiryContactUsEnquiryPurpose,
    EnquiryContactUsInput,
} from '@/graphql/api'
import {
    useCreateEnquiryContactUsExistingCustomerMutation,
    useCreateEnquiryContactUsNewCustomerMutation,
} from '@/graphql/api'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import { getUri } from '@/helpers/getCurrentUri'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'

import { useContactUs } from '@/modules/contact-us'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'

import ModalWithHeader from '@/components/layout/modal/ModalWithHeader.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import TextArea from '@/components/ui/inputs/TextArea.vue'
import ThSelectVue from '../ui/inputs/ThSelect.vue'
import EnquiryFormForm from './EnquiryFormForm.vue'
import FormRow from '@/components/ui/form/FormRow.vue'
import TransitionModal from '../layout/modal/TransitionModal.vue'
import { useRoutes } from '@/router/routes'
import { useIsAdmin } from '@/components/app/isAdmin'
import type { SelectOption } from '@/components/ui/inputs/th-select.ts'
import { useAuth } from '@/plugins/auth.ts'

const { t } = useI18n()
const { isOpen, close } = useContactUs()
const routes = useRoutes()

const formTitle = t('enquiry.form.contact_us.title')
const submitLabel = t('enquiry.form.contact_us.submit')

const purposeOptions = computed<SelectOption<EnquiryContactUsEnquiryPurpose>[]>(
    () => [
        {
            label: t('enquiry.form.contact_us.aftercare'),
            value: 'AFTERCARE',
        },
        {
            label: t('enquiry.form.contact_us.product'),
            value: 'PRODUCT',
        },
        {
            label: t('enquiry.form.contact_us.returning'),
            value: 'RETURNING',
        },
    ]
)

const purpose = ref<EnquiryContactUsEnquiryPurpose>('AFTERCARE')
const instructions = ref('')

const commissionJunction = getCommissionJunctionInput()
const uri = getUri()

const { isLogged } = useAuth()

const existingCustomerResponse =
    useCreateEnquiryContactUsExistingCustomerMutation()
const {
    executeMutation: contactUsExistingCustomerMutation,
    fetching: contactUsExistingCustomerFetching,
} = existingCustomerResponse

const newCustomerResponse = useCreateEnquiryContactUsNewCustomerMutation()
const {
    executeMutation: contactUsNewCustomerMutation,
    fetching: contactUsNewCustomerFetching,
} = newCustomerResponse

const fetching = computed(
    () =>
        contactUsExistingCustomerFetching.value ||
        contactUsNewCustomerFetching.value
)

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createEnquiryContactUsExistingCustomer'
    ),
    createResponseAndPath(
        newCustomerResponse,
        'createEnquiryContactUsNewCustomer'
    ),
])

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const isAdmin = useIsAdmin()

const doSubmit = () => {
    const input: EnquiryContactUsInput = {
        uri,
        purpose: purpose.value,
        instructions: instructions.value,
        commissionJunction,
    }

    if (isLogged.value) {
        return contactUsExistingCustomerMutation({ input })
    }

    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    return contactUsNewCustomerMutation({
        input,
        customer,
        isConsultant: isAdmin.value,
    })
}

const { routerPush } = useRouterNavigation()
const submit = () => {
    doSubmit()?.then((response) => {
        const { error } = response

        if (error) {
            return
        }

        routerPush(routes.designStudio())

        close()
    })
}
</script>
